import { type ChatFastAction } from "@do/shared-types";

/**
 * A solution button leads the user out of the conversation: a URL (an e-mail solution
 * is a `mailto:` URL), a phone number or a code snippet. Generative solutions reach
 * do-chat as regular URL/phone/e-mail buttons, so they are covered too.
 */
export function isSolutionButton(button: ChatFastAction): boolean {
  return !!(button.url || button.tel || button.code);
}

function getMetadataString(action: ChatFastAction, key: string): string | undefined {
  const entry = action.metadata?.find((item) => typeof item?.[key] === "string");
  return entry?.[key] as string | undefined;
}

function getSolLabelId(button: ChatFastAction): string {
  return getMetadataString(button, "sol_label_id") ?? "";
}

/**
 * A form the chatbot receives: a JSON object (it rejects anything else), other than the live chat
 * and ticketing forms do-chat handles itself (see ConversationStateDoBot).
 */
function isChatbotForm(form: unknown): boolean {
  return (
    typeof form === "object" &&
    form !== null &&
    !Array.isArray(form) &&
    (form as { isSupportForm?: unknown }).isSupportForm !== true
  );
}

/**
 * The solution click to report for a form submitted to the chatbot: the `doBot` step of a
 * Formulaires block sends the form as the first metadata entry, and the block's
 * `sol_label_id` and label (`sol_title`) in another one, left out of the `/workflow` request.
 * Live chat and ticketing forms never reach the chatbot, and neither does a form that is not a
 * JSON object (do-chat or the chatbot rejects it): `undefined` for those, as for any other action.
 */
export function getFormSubmissionClick(action: ChatFastAction): ChatFastAction | undefined {
  const form = action.metadata?.[0]?.["form"];
  if (typeof form !== "string") return undefined;

  try {
    // Same parsing as the server, which strips the line breaks first.
    if (!isChatbotForm(JSON.parse(form.replaceAll("\n", "")))) return undefined;
  } catch {
    return undefined;
  }

  return { metadata: action.metadata, title: getMetadataString(action, "sol_title") ?? "" };
}

/** The solution click to report for an action: a solution button, a submitted form, or none. */
export function getSolutionClickToReport(action: ChatFastAction): ChatFastAction | undefined {
  return isSolutionButton(action) ? action : getFormSubmissionClick(action);
}

/**
 * Builds the `/sol_clicked` command reporting a solution click to the chatbot.
 *
 * @example
 * buildSolutionClickCommand({ url: "https://x.y", title: "Voir l'offre", metadata: [{ sol_label_id: "abc" }] })
 * // '/sol_clicked{"sol_label_id":"abc","sol_title":"Voir l\'offre"}'
 */
export function buildSolutionClickCommand(button: ChatFastAction): string {
  return `/sol_clicked${JSON.stringify({
    sol_label_id: getSolLabelId(button),
    sol_title: button.title ?? "",
  })}`;
}
